// Pure configuration helpers for Firebase App Check provider selection.
// Kept in a side-effect-free module so unit tests can import them without
// triggering Firebase SDK initialization (`initializeApp` + `initializeAuth`)
// that runs at AuthWebContext load time.

export interface AppCheckProviderInputs {
  /** `ASSETRON_RECAPTCHA_SITE_KEY` from build-time config. */
  recaptchaSiteKey?: string | null;
  /**
   * Value of the Firebase App Check debug-token browser global — the literal
   * true sentinel, a debug token string, or unset. Playwright sets this before
   * the SPA bundle loads to switch the Firebase SDK into its debug provider.
   */
  debugTokenGlobal?: unknown;
  /** Whether we are running a production build (Vite PROD / NODE_ENV=production). */
  isProduction: boolean;
  /** Whether the caller has opted into an emulator or CI-disable escape hatch. */
  escapeHatchActive: boolean;
}

export type AppCheckProviderDecision =
  | { kind: 'skip-escape-hatch' }
  | { kind: 'prod-missing-recaptcha' }
  | { kind: 'dev-missing-all' }
  | { kind: 'activate-recaptcha'; siteKey: string }
  | { kind: 'activate-debug-placeholder' };

/** Truthy iff the SPA has a debug-token init flag in the browser global. */
export function hasBrowserDebugTokenInjection(
  debugTokenGlobal: unknown,
): boolean {
  return (
    debugTokenGlobal === true ||
    (typeof debugTokenGlobal === 'string' && debugTokenGlobal.length > 0)
  );
}

/**
 * Decide which App Check provider path to take. Pure function — all runtime
 * state is passed in. Returns a tagged union that the caller converts into
 * either a thrown error, a console.error, or an `initializeAppCheck` call.
 */
export function selectAppCheckProvider(
  inputs: AppCheckProviderInputs,
): AppCheckProviderDecision {
  if (inputs.escapeHatchActive) return { kind: 'skip-escape-hatch' };

  const hasRecaptchaKey = Boolean(inputs.recaptchaSiteKey?.trim());
  const hasDebugProvider = hasBrowserDebugTokenInjection(
    inputs.debugTokenGlobal,
  );

  // Production requires a real ReCaptcha site key regardless of any stray
  // debug-token globals, so a leaked FIREBASE_APPCHECK_DEBUG_TOKEN cannot
  // silently substitute for real attestation in a shipped bundle.
  if (inputs.isProduction && !hasRecaptchaKey) {
    return { kind: 'prod-missing-recaptcha' };
  }

  if (!inputs.isProduction && !hasRecaptchaKey && !hasDebugProvider) {
    return { kind: 'dev-missing-all' };
  }

  if (hasRecaptchaKey) {
    return {
      kind: 'activate-recaptcha',
      siteKey: inputs.recaptchaSiteKey!.trim(),
    };
  }

  // Non-prod debug-only path: the Firebase SDK's own debug provider kicks in
  // when FIREBASE_APPCHECK_DEBUG_TOKEN is set. We still need to supply a
  // non-empty value to `ReCaptchaEnterpriseProvider` so activation doesn't
  // error; the debug provider takes over the actual attestation.
  return { kind: 'activate-debug-placeholder' };
}

/** Canonical remediation message for dev-mode missing-provider errors. */
export const APP_CHECK_MISSING_PROVIDER_MESSAGE =
  '[AppCheck] No App Check provider is configured. The BE will reject ' +
  'every request with 401. Configure one of:\n' +
  '  1. ASSETRON_RECAPTCHA_SITE_KEY in your .env.* (production attestation), or\n' +
  '  2. A debug token injected by Playwright (see playwright/firebaseAppCheck.ts), or\n' +
  '  3. `cd assetron-sandbox && bin/appcheck-ensure-token.mjs` then source\n' +
  '     ~/.config/assetron/appcheck-env.sh and inject a debug token via the\n' +
  '     Firebase SDK global before the SPA loads.';

/** Canonical warning shown when an emulator/CI escape hatch is active. */
export const APP_CHECK_ESCAPE_HATCH_WARNING =
  '[AppCheck] Skipped: emulator or CI-disable escape hatch is on. ' +
  'The BE enforces App Check on every request — this build will see 401s ' +
  'on API calls. Unset ASSETRON_DISABLE_APP_CHECK_CI / ASSETRON_USE_FIREBASE_EMULATOR ' +
  'and source ~/.config/assetron/appcheck-env.sh (from bin/appcheck-ensure-token.mjs).';

/** Canonical error shown in production when RECAPTCHA_SITE_KEY is missing. */
export const APP_CHECK_PROD_MISSING_KEY_MESSAGE =
  '[AppCheck] Production build is missing ASSETRON_RECAPTCHA_SITE_KEY. ' +
  'BE requests will return 401. Rebuild with a real site key.';

/** Placeholder passed to `ReCaptchaEnterpriseProvider` when the Firebase SDK's
 * own debug provider will take over via a browser debug-token global. */
export const APP_CHECK_DEBUG_PROVIDER_PLACEHOLDER =
  'debug-provider-placeholder';

/**
 * Read the Firebase App Check debug-token value from the browser global (set
 * by Playwright's init script before the SPA bundle loads). Kept pure so it
 * can be unit-tested; the SPA passes `globalThis`.
 */
export function readDebugTokenGlobal(
  scope: { FIREBASE_APPCHECK_DEBUG_TOKEN?: unknown } | undefined,
): unknown {
  return scope?.FIREBASE_APPCHECK_DEBUG_TOKEN;
}

/**
 * True iff the current process is running in a production build (as declared
 * by NODE_ENV). Vite replaces this at build time; Node-side consumers get the
 * real env value. Kept pure so it can be unit-tested with a mock env.
 */
export function isProductionEnv(
  env: { NODE_ENV?: string } | undefined,
): boolean {
  return env?.NODE_ENV === 'production';
}

export type AppCheckEffect =
  | { kind: 'return-null' }
  | { kind: 'warn-then-null'; message: string }
  | { kind: 'error-then-null'; message: string }
  | { kind: 'throw'; message: string }
  | { kind: 'activate'; siteKey: string };

/**
 * Translate an {@link AppCheckProviderDecision} into an observable
 * {@link AppCheckEffect} — the thing a caller should actually do. Keeping
 * the effect-selection logic in a pure function lets us unit test the full
 * dispatch without stubbing the Firebase SDK.
 */
export function effectForDecision(
  decision: AppCheckProviderDecision,
  { isProduction }: { isProduction: boolean },
): AppCheckEffect {
  switch (decision.kind) {
    case 'skip-escape-hatch':
      return isProduction
        ? { kind: 'return-null' }
        : { kind: 'warn-then-null', message: APP_CHECK_ESCAPE_HATCH_WARNING };
    case 'prod-missing-recaptcha':
      return {
        kind: 'error-then-null',
        message: APP_CHECK_PROD_MISSING_KEY_MESSAGE,
      };
    case 'dev-missing-all':
      return { kind: 'throw', message: APP_CHECK_MISSING_PROVIDER_MESSAGE };
    case 'activate-recaptcha':
      return { kind: 'activate', siteKey: decision.siteKey };
    case 'activate-debug-placeholder':
      return {
        kind: 'activate',
        siteKey: APP_CHECK_DEBUG_PROVIDER_PLACEHOLDER,
      };
    default:
      return { kind: 'return-null' };
  }
}

export interface AppCheckExecutors<T> {
  /** Wrap the SDK-specific `initializeAppCheck(app, { provider })` call. */
  activate: (siteKey: string) => T;
  /** `console.warn` in the SPA, a spy in tests. */
  warn: (message: string) => void;
  /** `console.error` in the SPA, a spy in tests. */
  error: (message: string) => void;
}

/**
 * Execute an {@link AppCheckEffect} with caller-provided side-effect hooks.
 * Returns either `T` (an SDK AppCheck instance) or `null`, or throws for
 * the `throw` kind. Pure aside from the injected callbacks, so it is fully
 * unit-testable without pulling in the Firebase SDK.
 */
export function runAppCheckEffect<T>(
  effect: AppCheckEffect,
  executors: AppCheckExecutors<T>,
): T | null {
  switch (effect.kind) {
    case 'return-null':
      return null;
    case 'warn-then-null':
      executors.warn(effect.message);
      return null;
    case 'error-then-null':
      executors.error(effect.message);
      return null;
    case 'throw':
      throw new Error(effect.message);
    case 'activate':
      return executors.activate(effect.siteKey);
    default:
      return null;
  }
}
